<ng-template #loading>
    <mat-progress-bar color="primary" mode="indeterminate"></mat-progress-bar>
</ng-template>

<ng-template #notProvidedAllergies>
    <p *ngIf="patient.noAllergies === true">None Known</p>
    <p *ngIf="patient.noAllergies !== true">Not Provided</p>
</ng-template>
<ng-template #notProvidedHistory>
    <p *ngIf="patient.noMedicalHistory === true">None Known</p>
    <p *ngIf="patient.noMedicalHistory !== true">Not Provided</p>
</ng-template>
<ng-template #notProvidedMedication>
    <p *ngIf="patient.noMedicationHistory === true">None Known</p>
    <p *ngIf="patient.noMedicationHistory !== true">Not Provided</p>
</ng-template>

<mat-toolbar color="primary" class="action-toolbar">
    <button mat-icon-button (click)="returnToParent()" matTooltip="Back"><mat-icon
            color="primary">arrow_back_ios</mat-icon></button>
    <h3>{{ patient | patientHeading:'Patient File':'' }}</h3>
    <div class="toolbar-actions">
        <button mat-icon-button (click)="navigate($event, 'ai')"
            [matTooltip]="aiIsBusy ? 'We are currently processing your AI request. Please wait until this request is complete before submitting a new one.' : 'Create AI Notes'"
            *ngIf="patient && userRole !== 'Support-Limited'">
            <mat-icon *ngIf="!aiIsBusy" svgIcon="medic_ai_highlight"></mat-icon>
            <mat-icon *ngIf="aiIsBusy" class="spin" color="primary">sync</mat-icon>
        </button>

        <button mat-icon-button appWhatsappDialogTrigger [patientId]="patient?.id" [patient]="patient"
            matTooltip="Open WhatsApp chat" *ngIf="patient && !stateMode">
            <mat-icon svgIcon="whatsapp"></mat-icon>
        </button>

        <ng-container *ngIf="patient && !isMobile && hasHospitals">
            <button mat-icon-button *ngIf="patient.status === 'opd'" (click)="navigate($event, 'admitPatient')"
                matTooltip="Admit Patient">
                <mat-icon svgIcon="in_out"></mat-icon>
            </button>
            <button mat-icon-button *ngIf="patient.status === 'inpatient'"
                (click)="navigate($event, 'dischargePatient')" matTooltip="Discharge Patient">
                <mat-icon svgIcon="in_out"></mat-icon>
            </button>
        </ng-container>
        <button mat-icon-button (click)="navigate($event, 'managePatient')" matTooltip="Edit Patient"
            *ngIf="patient && !patient.requiresValidation && !patient.requiresICDValidation && !patient.wasSelfBoarded">
            <mat-icon color="primary">edit</mat-icon>
        </button>
    </div>
</mat-toolbar>
<div class="main-container" id="details">
    <div class="content-container">
        <div class="patient-file-container">
            <mat-card class="m-b-20">
                <ng-container *ngIf="patient; else: loading">
                    <!--
                      The card's own actions, top-right. Referrals sits beside the export because both are "this whole
                      file, out of here": the `share` icon is the one the Referral Network settings tab carries, and the
                      badge counts who is actively receiving THIS patient - not the practice's referral network, which
                      says nothing about this file.
                    -->
                    <div class="file-card-actions">
                        <!-- The practice's chat about this patient (messaging hub, tasks/messaging-hub-architecture.md §7),
                             beside Referrals (owner, 2026-09-21): both are conversations about this file. -->
                        <button mat-icon-button (click)="$event.stopPropagation(); openPatientChat()" matTooltip="Chats about this patient"
                            aria-label="Chats about this patient" *ngIf="(chatService.enabled$ | async) !== false" data-test="open-patient-chat">
                            <mat-icon color="primary">forum</mat-icon>
                        </button>
                        <button mat-icon-button *ngIf="patient && referralsEnabled && userRole !== 'Support-Limited'"
                            (click)="openReferralDialog($event)" data-test="open-referrals"
                            [matTooltip]="activeReferralLinks ? 'Referrals · shared with ' + activeReferralLinks + (activeReferralLinks === 1 ? ' clinician or practice' : ' clinicians or practices') : 'Referrals'">
                            <mat-icon color="primary" [matBadge]="activeReferralLinks" [matBadgeHidden]="!activeReferralLinks"
                                matBadgeSize="small" matBadgeColor="accent" data-test="referral-link-badge">share</mat-icon>
                        </button>
                        <button mat-icon-button class="file-download-button" *ngIf="isGroupAdmin"
                            [matMenuTriggerFor]="exportMenu" matTooltip="Download Full Patient File"
                            [disabled]="isExportingFile">
                            <mat-icon color="primary">{{ isExportingFile ? 'hourglass_empty' : 'file_download' }}</mat-icon>
                        </button>
                        <!-- Linked to patient app (tasks/patient-app.md §4.5, P8). Shown only for a linked file of a practice that
                             opted in; an admin can end the link from here (§5.3). -->
                        <button mat-icon-button *ngIf="patientApp?.linked && !stateMode" data-test="patient-app-linked"
                            (click)="$event.stopPropagation()" [matMenuTriggerFor]="patientApp?.canEnd ? patientAppMenu : null"
                            [matTooltip]="'Linked to patient app' + (patientApp?.linkedAt ? ' since ' + (patientApp?.linkedAt | date: 'd MMM y') : '')"
                            aria-label="Linked to patient app">
                            <mat-icon color="primary">phonelink_ring</mat-icon>
                        </button>
                        <mat-menu #patientAppMenu="matMenu">
                            <button mat-menu-item (click)="endPatientAppLink()" data-test="end-patient-app-link">
                                <mat-icon>link_off</mat-icon>
                                <span>End patient app link</span>
                            </button>
                        </mat-menu>
                    </div>

                    <!-- Choose what the export contains. Every toggle is re-validated server-side;
                         the audio one is hidden without permission AND refused by the server.

                         `xPosition="before"` because the trigger sits at the card's top-right:
                         the panel's right edge aligns to the button and it grows LEFTWARD, into
                         the page, rather than off the edge of the screen. -->
                    <mat-menu #exportMenu="matMenu" class="export-options-menu" xPosition="before">
                        <div class="export-options" (click)="$event.stopPropagation()">
                            <div class="export-options-header">
                                <span class="export-options-title">Include in export</span>
                                <button mat-button color="primary" type="button" class="export-options-toggle-all"
                                    (click)="setAllExportSections(selectedExportSectionCount !== exportSections.length)">
                                    {{ selectedExportSectionCount === exportSections.length ? 'Clear all' : 'Select all' }}
                                </button>
                            </div>

                            <mat-slide-toggle *ngFor="let section of exportSections" class="export-option"
                                [checked]="exportOptions.sections[section.key] !== false"
                                (change)="toggleExportSection(section.key, $event.checked)">
                                {{ section.label }}
                            </mat-slide-toggle>

                            <mat-divider class="export-options-divider"></mat-divider>

                            <ng-container *ngIf="canIncludeRecordings">
                                <mat-slide-toggle class="export-option" [checked]="exportOptions.includeAudio"
                                    (change)="toggleExportAudio($event.checked)">
                                    Audio recordings
                                </mat-slide-toggle>
                                <p class="export-option-hint" [class.warn]="exportOptions.includeAudio">
                                    <ng-container *ngIf="!exportOptions.includeAudio">
                                        The original consultation audio, rejoined into one file per consultation.
                                    </ng-container>
                                    <ng-container *ngIf="exportOptions.includeAudio">
                                        <mat-icon inline>schedule</mat-icon>
                                        This export will take considerably longer and the file will be much larger.
                                        Each consultation adds tens of megabytes and is reassembled one at a time,
                                        so a patient with many recordings can take several minutes.
                                    </ng-container>
                                </p>
                            </ng-container>

                            <p class="export-option-hint" *ngIf="hasNoExportSelection">
                                Nothing is selected — the export would contain only the patient's own details.
                            </p>

                            <button mat-raised-button color="primary" class="export-options-start" type="button"
                                (click)="exportPatientFile()">
                                <mat-icon>file_download</mat-icon>
                                Start export
                            </button>
                        </div>
                    </mat-menu>

                    <div class="requires-validation-banner" *ngIf="patient && patient.requiresValidation">
                        <mat-icon class="bolt-icon">bolt</mat-icon>
                        <div class="messages">
                            <p><strong>Incomplete Patient Record:</strong> This record was created using Express Add.
                                Please review and complete all necessary details to ensure the medical information
                                is accurate and up to date.</p>
                            <button mat-raised-button (click)="navigate($event, 'managePatient')">
                                <mat-icon>edit</mat-icon>
                                Review Record
                            </button>
                        </div>
                    </div>

                    <div class="requires-validation-banner" *ngIf="patient && patient.wasSelfBoarded">
                        <mat-icon class="bolt-icon">medical_information</mat-icon>
                        <div class="messages">
                            <p><strong>Patient Information Requires Review:</strong> Following patient self-boarding the
                                information in this file has not been validated. Please review, complete missing
                                information and press save to validate this file as complete </p>
                            <button mat-raised-button (click)="navigate($event, 'managePatient')">
                                <mat-icon>edit</mat-icon>
                                Review Record
                            </button>
                        </div>
                    </div>

                    <div class="requires-validation-banner" *ngIf="patient.requiresICDValidation">
                        <mat-icon class="bolt-icon">warning_amber</mat-icon>
                        <div class="messages">
                            <p><strong>Diagnosis and ICD-10 Codes Require Review:</strong> This record was automatically
                                coded for ICD-10 with "Z00" and diagnosis was set to "UNSPECIFIED". Please review the
                                diagnosis and update the ICD-10 codes.</p>
                            <button mat-raised-button (click)="navigate($event, 'managePatient')">
                                <mat-icon>edit</mat-icon>
                                Review Diagnosis/ICD-10
                            </button>
                        </div>
                    </div>

                    <div class="requires-validation-banner" *ngIf="patient.status === 'invite'">
                        <mat-icon class="bolt-icon">mail</mat-icon>
                        <div class="messages">
                            <p><strong>Pending Invite:</strong> This file is still in a pending invite state. Please
                                ensure the patient completes the invite process to ensure accurate and complete patient
                                information.</p>
                        </div>
                    </div>

                    <!-- Mortality banner if appropriate showing patient demised -->
                    <div class="mortality-banner" *ngIf="patient.isDeceased">
                        <mat-icon class="bolt-icon">medical_information</mat-icon>
                        <div class="messages">
                            <p><strong>Patient Demised:</strong> This patient has been declared deceased.
                            </p>
                        </div>
                    </div>


                    <div class="patient-primary-info">
                        <div class="file-column">
                            <div class="demographics">
                                <h2>{{ patient.name }} {{ patient.surname }} ({{ patient.patientNumber }})</h2>
                                <p> {{ patient.dob }} ({{ patient.dob | ageFromDate }})</p>

                                <p>{{ patient.gender }}</p>
                                <p><a href="mailto:{{ patient.email }}" target="_blank" rel="noopener">{{ patient.email }}</a></p>
                                <p><a href="tel:{{ patient.mobileNumber }}" (click)="dialPhone($event, patient.mobileNumber)" target="_blank" rel="noopener">{{ patient.mobileNumber }}</a></p>
                                <p><a href="tel:{{ patient.phoneNumber }}" (click)="dialPhone($event, patient.phoneNumber)" target="_blank" rel="noopener">{{ patient.phoneNumber }}</a></p>
                            </div>
                            <div class="file-actions">
                                <button mat-raised-button class="cura-button"
                                    matTooltip="Ask Cura, your medical AI assistant, about this patient file"
                                    (click)="openAssistantDialog()" *ngIf="userRole !== 'Support-Limited'">
                                    <mat-icon>assistant</mat-icon>
                                    Ask Cura
                                </button>


                            </div>
                        </div>
                        <mat-divider></mat-divider>
                        <div class="info-group">
                            <h3>Diagnosis/Active Problems</h3>
                            <p>{{ patient.diagnosis }}</p>
                            <p *ngFor="let diagnosis of getDiagnosisArray(patient)">
                                {{ diagnosis }}
                            </p>

                            <!-- `icdArchivedCount` is in the condition on purpose. A patient whose
                                 coding has ALL been archived has no chips, no suggestions and
                                 possibly no summary — and would lose the heading entirely, which is
                                 the one case where reaching the history matters most. -->
                            <!-- ⚠ GUARDED ON THE PATIENT'S CODES AS WELL AS ON `icdChips`.
                                 `icdChips` is derived. If building it ever yields nothing while
                                 the record HAS codes, keying the section off it alone hides the
                                 heading, the pencil, the review badge and the archive together
                                 with the chips — turning a rendering fault into "this patient has
                                 no coding", and taking away the very control a clinician would
                                 reach for to check. The raw array answers the honest question:
                                 does this record have codes? -->
                            <ng-container
                                *ngIf="(patient.icdCodes && patient.icdCodes.length) || icdChips.length || icdPendingCount > 0 || icdSummaryCount > 0 || icdArchivedCount > 0">
                                <h3 class="icd-heading">
                                    <span class="icd-heading-text">ICD-10 Codes</span>
                                    <!-- Immediately after the heading, ahead of the indicators.
                                         It is the one ACTION here — the badge and the summary
                                         icon report state — and the only one always present, so
                                         a fixed position next to the label keeps it where the
                                         eye learns to find it rather than shifting as the
                                         others appear and disappear. -->
                                    <button mat-icon-button class="icd-edit-button" color="primary"
                                        *ngIf="canEditIcdCodes"
                                        (click)="openIcdCodeEditDialog($event)"
                                        matTooltip="Edit ICD-10 codes">
                                        <!-- `color="primary"` on the BUTTON only, exactly as the
                                             review button beside it does. That sets
                                             `--mdc-icon-button-icon-color` through the theme,
                                             which is what actually colours the glyph — a plain
                                             `color:` in CSS loses to Material's own styles. -->
                                        <mat-icon>edit</mat-icon>
                                    </button>
                                    <!-- §11 — run the coding pipeline over this patient's whole
                                         record. Beside the pencil because both are ACTIONS on the
                                         coding, and ahead of the indicators for the same reason
                                         the pencil is.

                                         Disabled rather than hidden while a run is in flight: a
                                         control that disappears mid-task reads as a bug, and the
                                         tooltip on the disabled button is where the run's stage
                                         is reported. `icdRunInFlight` covers the click-to-response
                                         window as well as the run itself, so a double click
                                         cannot get past it — though the server's 409 is what
                                         actually guarantees that across tabs and devices. -->
                                    <button mat-icon-button class="icd-run-button" color="primary"
                                        *ngIf="canRunIcdPipeline"
                                        [disabled]="icdRunInFlight"
                                        (click)="runIcdPipeline($event)"
                                        [matTooltip]="icdRunTooltip">
                                        <!-- The spinner REPLACES the glyph rather than sitting
                                             beside it, so the button keeps its box and the row of
                                             icons does not reflow the moment a run starts. -->
                                        <mat-spinner *ngIf="icdRunInFlight; else icdRunIdleIcon"
                                            diameter="18"></mat-spinner>
                                        <ng-template #icdRunIdleIcon>
                                            <mat-icon>auto_awesome</mat-icon>
                                        </ng-template>
                                    </button>
                                    <!-- §11 — the entry point to the review queue. Shown whenever
                                         suggestions are waiting, including when auto-accept is on:
                                         removals are never applied automatically (§7 rule 6), so
                                         there is always something only a clinician can decide. -->
                                    <button mat-icon-button class="icd-suggestions-button"
                                        *ngIf="icdPendingCount > 0" color="primary"
                                        (click)="openIcdSuggestionsDialog($event)"
                                        [matTooltip]="icdPendingCount + ' coding suggestion(s) waiting for review'">
                                        <mat-icon [matBadge]="icdPendingCount" matBadgeColor="accent"
                                            matBadgeSize="small">rate_review</mat-icon>
                                    </button>
                                    <!-- §11.14 — the standing clinical context. The tooltip says
                                         AI-generated before the click, not only inside the dialog:
                                         an info icon beside a clinical heading otherwise reads as
                                         part of the record. -->
                                    <button mat-icon-button class="icd-summary-button"
                                        *ngIf="icdSummaryCount > 0"
                                        (click)="openClinicalSummaryDialog($event)"
                                        matTooltip="AI-generated clinical summary — background context drawn from this patient's notes, not part of the record">
                                        <mat-icon>info_outline</mat-icon>
                                    </button>
                                    <!-- Last, and muted like the summary icon beside it: codes are
                                         never hard-deleted, so this is standing history rather than
                                         anything waiting on a decision. Only rendered when the
                                         patient actually has archived codes — an always-present
                                         icon that usually opens an empty list trains people to
                                         stop clicking it. -->
                                    <button mat-icon-button class="icd-archive-button"
                                        *ngIf="icdArchivedCount > 0"
                                        (click)="openIcdArchiveDialog($event)"
                                        [matTooltip]="icdArchivedCount + ' previously used code(s) — no longer on the record'">
                                        <mat-icon>history</mat-icon>
                                    </button>
                                </h3>
                                <!-- `icdChips`, not `patient.icdCodes`: ordered by the persisted
                                     `sequence` and carrying the states resolved once on load.
                                     See `buildIcdChips` for why this is not done in the
                                     template. -->
                                <ng-container *ngFor="let chip of icdChips; let i = index">
                                    <div mat-chip class="icd-chip"
                                        [ngClass]="'icd-state--' + (chip.view.primaryState || 'none')"
                                        [class.icd-state--primary]="i === 0"
                                        [class.clickable]="chip.code.source === 'ai-auto' || chip.code.flaggedForRemoval"
                                        (click)="openIcdCodeHistory(chip.code, $event)"
                                        [matTooltip]="chip.view.tooltip" matTooltipClass="icd-tooltip">
                                        <!-- Every part is an ELEMENT, including the code itself. A
                                             bare text node becomes an anonymous flex item that
                                             takes the container's line-height rather than one we
                                             set, which is what left the marker, the code and the
                                             date sitting on three different baselines. -->
                                        <span class="icd-primary-marker" *ngIf="i === 0">1&deg;</span>
                                        <span class="icd-chip-code">{{ chip.code.code }}</span>
                                        <!-- Truncates; the date and the state icons do not. A code
                                             on its own tells a clinician nothing they can read at
                                             a glance — "C71.9" needs the description to mean
                                             anything without hovering. -->
                                        <span class="icd-chip-desc" *ngIf="chip.code.description">
                                            {{ chip.code.description }}
                                        </span>
                                        <!-- The date is a clinical and a billing fact, and it was
                                             readable nowhere on this screen. Shown on the chip
                                             because the card carries no description to truncate
                                             it out of view. -->
                                        <span class="icd-chip-date" [class.unset]="!chip.view.onsetLabel">
                                            {{ chip.view.onsetLabel || 'no date' }}
                                        </span>
                                        <mat-icon class="icd-state-icon" *ngFor="let state of chip.view.iconStates"
                                            [ngClass]="state" [ngSwitch]="state">
                                            <ng-container *ngSwitchCase="'error'">error_outline</ng-container>
                                            <ng-container *ngSwitchCase="'warning'">warning_amber</ng-container>
                                            <ng-container *ngSwitchCase="'flagged'">flag</ng-container>
                                            <ng-container *ngSwitchCase="'ai-applied'">auto_awesome</ng-container>
                                            <ng-container *ngSwitchCase="'pmb'">shield</ng-container>
                                        </mat-icon>
                                    </div>
                                </ng-container>
                            </ng-container>


                            <h3 class="m-t-10">Allergies/Alerts</h3>
                            <ng-container
                                *ngIf="patient.allergies && patient.allergies.length; else: notProvidedAllergies">
                                <ng-container *ngFor="let allergy of patient.allergies; let last = last">
                                    <div mat-chip class="red-chip"
                                        [matTooltip]="withReported(allergy.allergy + ' - ' + (allergy.reaction || ''), allergy)">{{allergy.allergy}}
                                        <mat-icon *ngIf="reported(allergy)" class="patient-reported-mark" aria-label="Patient reported">person</mat-icon>
                                    </div>
                                </ng-container>
                            </ng-container>

                            <h3 class="m-t-10">Medical History</h3>
                            <p *ngIf="patient.weight || patient.height">
                                <span *ngIf="patient.weight">Weight: {{patient.weight}}kg</span>
                                <span *ngIf="patient.height"> Height: {{patient.height}}cm</span>
                                <span *ngIf="patient.bmi"> BMI: {{patient.bmi}}</span>
                            </p>
                            <ng-container
                                *ngIf="patient.medicalHistory && patient.medicalHistory.length; else: notProvidedHistory">
                                <ng-container *ngFor="let history of patient.medicalHistory; let last = last">
                                    <div mat-chip class="green-chip"
                                        [matTooltip]="withReported(history.surgery + ' - ' + (history.year || ''), history)">{{history.surgery}}
                                        <mat-icon *ngIf="reported(history)" class="patient-reported-mark" aria-label="Patient reported">person</mat-icon>
                                    </div>
                                </ng-container>
                            </ng-container>


                            <!-- Content that should be hidden for Physiotherapy -->
                            <h3 class="m-t-10">Medication List</h3>
                            <div class="full-width"><span>Patient Reported</span></div>
                            <div class="full-width"
                                *ngIf="patient.medicationHistory && patient.medicationHistory.length; else: notProvidedMedication">
                                <ng-container *ngFor="let medication of patient.medicationHistory; let last = last">
                                    <div mat-chip class="orange-chip"
                                        [matTooltip]="withReported(medication.medication + ' - ' + (medication.dosage || ''), medication)">
                                        {{medication.medication}}
                                        <mat-icon *ngIf="reported(medication)" class="patient-reported-mark" aria-label="Patient reported">person</mat-icon>
                                    </div>
                                </ng-container>
                            </div>
                            <div class="full-width" *ngIf="activeMedication?.length"
                                [appGroupSpecialtyHide]="{ specialties: ['Physiotherapy'], condition: 'exclude' }">
                                <span>Active Medication</span>
                            </div>
                            <div class="full-width" *ngIf="activeMedication?.length"
                                [appGroupSpecialtyHide]="{ specialties: ['Physiotherapy'], condition: 'exclude' }">
                                <ng-container *ngFor="let med of activeMedication; let last = last">
                                    <div mat-chip class="orange-chip" matTooltip="{{ med.dose }} {{ med.unit }} {{ med.frequency }}
                                        {{ med.duration }}">
                                        {{med.name || med.medication}}
                                    </div>
                                </ng-container>
                            </div>

                            <ng-container *ngIf="patient.medicalTeam && patient.medicalTeam.length">
                                <h3 class="m-t-10">Medical Team</h3>
                                <ng-container *ngFor="let member of patient.medicalTeam; let last = last">
                                    <div mat-chip
                                        [matTooltip]="withReported('Speciality: ' + (member.speciality || 'Not Provided') + ', Email: ' + (member.email || 'Not Provided') + ', Phone: ' + (member.phone || 'Not Provided'), member)">
                                        {{member.title}} {{member.name}} {{member.surname}}
                                        <mat-icon *ngIf="reported(member)" class="patient-reported-mark" aria-label="Patient reported">person</mat-icon>
                                        <button matChipTrailingIcon class="pointer" *ngIf="!colleagueExists(member)"
                                            matTooltip="Add this physician to my network"
                                            (click)="addColleague(member)">
                                            <mat-icon color="primary">add</mat-icon>
                                        </button>
                                    </div>
                                </ng-container>
                            </ng-container>


                            <ng-container
                                *ngIf="consentConditions?.items?.length || consentConditions?.bloodTransfusion">
                                <h3 class="m-t-10">Consent Responses</h3>
                                <div mat-chip class="red-chip truncate-chip"
                                    *ngIf="consentConditions.bloodTransfusion == 'NO'"
                                    matTooltip="This patient has indicated that they DO NOT consent to a blood transfusion">
                                    NO BLOOD</div>
                                <ng-container *ngFor="let response of consentConditions.items; let last = last">
                                    <div mat-chip [matTooltip]="response.text"
                                        [class.green-chip]="response.category === 'positive'"
                                        [class.red-chip]="response.category === 'negative'" class="truncate-chip">
                                        {{response.shortText || response.text}}
                                    </div>
                                </ng-container>
                            </ng-container>

                        </div>

                        <ng-container *ngIf="patient.admissionDate && patient.status === 'inpatient'">
                            <mat-divider class="m-t-10"></mat-divider>
                            <div class="info-group">
                                <h3>Admission Details</h3>
                                <p>{{ patient.admissionDate }} to present ({{
                                    patient.admissionDate | dateDiff
                                    }})</p>
                                <p>{{ patient.admissionType }}</p>
                                <p>{{ patient.admissionSummary }}</p>
                                <p>{{ patient.hospitalName }}</p>
                                <p>{{patient.wardName}} <span *ngIf="patient.bed">(Bed: {{ patient.bed }})</span></p>

                            </div>
                        </ng-container>
                        <ng-container *ngIf="authorisations.length">
                            <mat-divider class="m-t-10"></mat-divider>
                            <div class="info-group">
                                <h3>Authorisations</h3>
                                <p *ngFor="let line of authorisations">{{ line }}</p>
                            </div>
                        </ng-container>

                    </div>

                    <mat-accordion class="m-t-10">
                        <mat-expansion-panel class="flat fill">
                            <mat-expansion-panel-header>
                                <mat-panel-title>ADDITIONAL INFORMATION</mat-panel-title>
                            </mat-expansion-panel-header>

                            <div class="patient-additional-info">
                                <div class="info-group">
                                    <h3>Identification</h3>
                                    <p>ID Number: {{ patient.idNumber }}</p>
                                    <p>Passport Number: {{ patient.passportNumber }}</p>
                                    <p *ngIf="patient.passportCountry">Passport Country: {{ patient.passportCountry }}
                                    </p>
                                </div>

                                <div class="info-group">
                                    <h3>Personal Details</h3>
                                    <p>Address: {{ patient.address }}</p>
                                    <p>Post Code: {{ patient.addressCode }}</p>
                                    <p>Religion: {{ patient.religion }}</p>
                                    <p>Language: {{ patient.language }}</p>
                                    <p>Occupation: {{ patient.occupation }}</p>
                                    <p>Employer: {{ patient.employer }}</p>
                                    <p>Work Phone: <a href="tel:{{ patient.workPhone }}" (click)="dialPhone($event, patient.workPhone)" target="_blank" rel="noopener">{{ patient.workPhone }}</a></p>
                                    <p>Work Address: {{ patient.workAddress }}</p>
                                    <p>Post Code: {{ patient.workAddressCode }}</p>
                                    <p>Marital Status: {{ patient.maritalStatus }}</p>
                                </div>

                                <div class="info-group">
                                    <h3>Medical Aid</h3>
                                    <p>Medical Aid/Insurance: {{ medicalAidDisplay(patient).name }}</p>
                                    <p>Medical Aid/Insurance Plan: {{ medicalAidDisplay(patient).plan }}</p>
                                    <p>Medical Aid/Insurance Number: {{ patient.medicalAidNumber }}</p>
                                    <p>Main Member: {{ patient.medicalMainMember }}</p>
                                    <p>Dependant Code: {{ patient.medicalDependantCode }}</p>
                                    <p>Date Joined: {{ patient.medicalDateJoined }}</p>
                                </div>

                                <div class="info-group">
                                    <h3>Gap Cover</h3>
                                    <p>Gap Cover: {{ patient.gapCover }}</p>
                                    <p>Policy Number: {{ patient.gapCoverNumber }}</p>
                                </div>


                                <div class="info-group">
                                    <h3>Person Responsible For Account</h3>
                                    <p>Title: {{ patient.liableTitle }}</p>
                                    <p>Initials: {{ patient.liableInitials }}</p>
                                    <p>Name: {{ patient.liableName }}</p>
                                    <p>Surname: {{ patient.liableSurname }}</p>
                                    <p>ID Type: {{ patient.liableIdType }}</p>
                                    <p>ID Number: {{ patient.liableIdNumber }}</p>
                                    <p>Passport Number: {{ patient.liablePassportNumber }}</p>
                                    <p *ngIf="patient.liablePassportCountry">Passport Country: {{
                                        patient.liablePassportCountry }}
                                    </p>
                                    <p>Email: <a href="mailto:{{ patient.liableEmail }}" target="_blank" rel="noopener">{{ patient.liableEmail }}</a></p>
                                    <p>Mobile Number: <a href="tel:{{ patient.liableMobileNumber }}" (click)="dialPhone($event, patient.liableMobileNumber)" target="_blank" rel="noopener">{{ patient.liableMobileNumber }}</a></p>
                                    <p>Phone Number: <a href="tel:{{ patient.liablePhoneNumber }}" (click)="dialPhone($event, patient.liablePhoneNumber)" target="_blank" rel="noopener">{{ patient.liablePhoneNumber }}</a></p>
                                    <p>Address: {{ patient.liableAddress }}</p>
                                    <p>Post Code: {{ patient.liableAddressCode }}</p>
                                    <p>Occupation: {{ patient.liableOccupation }}</p>
                                    <p>Employer: {{ patient.liableEmployer }}</p>
                                    <p>Work Phone: <a href="tel:{{ patient.liableWorkPhone }}" (click)="dialPhone($event, patient.liableWorkPhone)" target="_blank" rel="noopener">{{ patient.liableWorkPhone }}</a></p>
                                    <p>Work Address: {{ patient.liableWorkAddress }}</p>
                                    <p>Post Code: {{ patient.liableWorkAddressCode }}</p>
                                </div>


                                <div class="info-group">
                                    <h3>Next of Kin 1</h3>
                                    <p>Name: {{ patient.emergencyContact }}</p>
                                    <p>Relationship: {{ patient.emergencyContactRelationship }}</p>
                                    <p>Mobile: <a href="tel:{{ patient.emergencyContactMobile }}" (click)="dialPhone($event, patient.emergencyContactMobile)" target="_blank" rel="noopener">{{ patient.emergencyContactMobile }}</a></p>
                                    <p>Phone: <a href="tel:{{ patient.emergencyContactPhone }}" (click)="dialPhone($event, patient.emergencyContactPhone)" target="_blank" rel="noopener">{{ patient.emergencyContactPhone }}</a></p>
                                    <p>Work: <a href="tel:{{ patient.emergencyContactWork }}" (click)="dialPhone($event, patient.emergencyContactWork)" target="_blank" rel="noopener">{{ patient.emergencyContactWork }}</a></p>
                                    <p>Email: <a href="mailto:{{ patient.emergencyContactEmail }}" target="_blank" rel="noopener">{{ patient.emergencyContactEmail }}</a></p>
                                    <p>Address: {{ patient.emergencyContactAddress }}</p>
                                    <p>Post Code: {{ patient.emergencyContactAddressCode }}</p>
                                </div>

                                <div class="info-group">
                                    <h3>Next of Kin 2</h3>
                                    <p>Name: {{ patient.emergencyContact2 }}</p>
                                    <p>Relationship: {{ patient.emergencyContactRelationship2 }}</p>
                                    <p>Mobile: <a href="tel:{{ patient.emergencyContactMobile2 }}" (click)="dialPhone($event, patient.emergencyContactMobile2)" target="_blank" rel="noopener">{{ patient.emergencyContactMobile2 }}</a></p>
                                    <p>Phone: <a href="tel:{{ patient.emergencyContactPhone2 }}" (click)="dialPhone($event, patient.emergencyContactPhone2)" target="_blank" rel="noopener">{{ patient.emergencyContactPhone2 }}</a></p>
                                    <p>Work: <a href="tel:{{ patient.emergencyContactWork2 }}" (click)="dialPhone($event, patient.emergencyContactWork2)" target="_blank" rel="noopener">{{ patient.emergencyContactWork2 }}</a></p>
                                    <p>Email: <a href="mailto:{{ patient.emergencyContactEmail2 }}" target="_blank" rel="noopener">{{ patient.emergencyContactEmail2 }}</a></p>
                                    <p>Address: {{ patient.emergencyContactAddress2 }}</p>
                                    <p>Post Code: {{ patient.emergencyContactAddressCode2 }}</p>
                                </div>


                            </div>
                        </mat-expansion-panel>
                    </mat-accordion>
                </ng-container>
            </mat-card>

            <mat-accordion multi="true" *ngIf="patient">

                <app-patient-clinical-timeline id="timeline" [initialExpanded]="isExpanded('timeline')"
                    (expandedChange)="togglePanel('timeline', $event)"></app-patient-clinical-timeline>

                <app-patient-ai-request-list id="ai_request" [initialExpanded]="isExpanded('ai_request')"
                    (expandedChange)="togglePanel('ai_request', $event)"></app-patient-ai-request-list>

                <app-prescription-list id="prescription" [initialExpanded]="isExpanded('prescription')"
                    (expandedChange)="togglePanel('prescription', $event)"
                    [appGroupSpecialtyHide]="{ specialties: ['Physiotherapy'], condition: 'exclude' }"></app-prescription-list>

                <app-lab-orders-list id="lab_orders" [initialExpanded]="isExpanded('lab_orders')"
                    (expandedChange)="togglePanel('lab_orders', $event)"></app-lab-orders-list>

                <app-patient-appointment-list id="appointments" [initialExpanded]="isExpanded('appointments')"
                    (expandedChange)="togglePanel('appointments', $event)"></app-patient-appointment-list>

                <app-patient-task-list id="tasks" [initialExpanded]="isExpanded('tasks')"
                    (expandedChange)="togglePanel('tasks', $event)"></app-patient-task-list>

                <!-- Workstream L. This section REPLACES the timeline placement of risk items
                     (D11, withdrawn) — the rows live here, grouped by date and filterable by
                     category, and no longer in the clinical timeline. -->
                <app-patient-risk-list id="risk" [initialExpanded]="isExpanded('risk')"
                    (expandedChange)="togglePanel('risk', $event)"></app-patient-risk-list>

                <app-patient-billing-list id="billing" *ngIf="showCoding" [initialExpanded]="isExpanded('billing')"
                    (expandedChange)="togglePanel('billing', $event)"></app-patient-billing-list>

                <app-patient-invoicing-list id="invoicing" *ngIf="!stateMode" [initialExpanded]="isExpanded('invoicing')"
                    (expandedChange)="togglePanel('invoicing', $event)"></app-patient-invoicing-list>

                <app-patient-consent-list id="consent" [initialExpanded]="isExpanded('consent')"
                    (expandedChange)="togglePanel('consent', $event)"></app-patient-consent-list>

                <!-- Practice forms — hidden pending a rollout decision (tasks/patient-forms.md §17).
                     Nothing deleted; environment.PRACTICE_FORMS_ENABLED restores it. -->
                <app-patient-form-list *ngIf="practiceFormsEnabled" id="forms"
                    [initialExpanded]="isExpanded('forms')"
                    (expandedChange)="togglePanel('forms', $event)"></app-patient-form-list>

                <!-- Practice documents (tasks/practice-documents.md 7 phase 2). No feature flag:
                     the Forms panel above is hidden pending a rollout decision, documents ship. -->
                <app-patient-document-list id="documents"
                    [initialExpanded]="isExpanded('documents')"
                    (expandedChange)="togglePanel('documents', $event)"></app-patient-document-list>

                <app-patient-communication-list id="communication" [initialExpanded]="isExpanded('communication')"
                    (expandedChange)="togglePanel('communication', $event)"></app-patient-communication-list>

                <app-patient-survey-responses id="surveys" *ngIf="showSurveys" [initialExpanded]="isExpanded('surveys')" [patient]="patient"
                    [selectedGroup]="selectedGroup"
                    (expandedChange)="togglePanel('surveys', $event)"></app-patient-survey-responses>

                <app-patient-media-list id="media" [initialExpanded]="isExpanded('media')"
                    (expandedChange)="togglePanel('media', $event)"></app-patient-media-list>



                <mat-expansion-panel [expanded]="isExpanded('tags')" (opened)="togglePanel('tags', true)"
                    (closed)="togglePanel('tags', false)" id="tags">
                    <mat-expansion-panel-header>
                        <mat-panel-title>PATIENT TAGS</mat-panel-title>
                    </mat-expansion-panel-header>
                    <mat-form-field class="full-width m-t-20" appearance="outline">
                        <mat-label>Tags</mat-label>
                        <mat-chip-grid #chipGrid aria-label="Enter Tag">
                            <mat-chip-row *ngFor="let tag of patientTags; let i = index" (removed)="removeTag(tag)"
                                [editable]="false">
                                {{ tag.tag }}
                                <button matChipRemove>
                                    <mat-icon>cancel</mat-icon>
                                </button>
                            </mat-chip-row>
                            <input #tagInputElement placeholder="New Tag..." [matChipInputFor]="chipGrid"
                                [matChipInputSeparatorKeyCodes]="separatorKeysCodes" [matChipInputAddOnBlur]="addOnBlur"
                                [formControl]="tagControl" [matAutocomplete]="auto"
                                (matChipInputTokenEnd)="onTagInputTokenEnd($event)" />
                        </mat-chip-grid>
                        <mat-autocomplete #auto="matAutocomplete" (optionSelected)="addTag($event.option.value)">
                            <mat-option *ngFor="let tag of filteredTags | async" [value]="tag">
                                {{ tag }}
                            </mat-option>
                        </mat-autocomplete>
                    </mat-form-field>
                </mat-expansion-panel>
            </mat-accordion>

            <div class="mobile-spacer"></div>

        </div>
    </div>
    <div class="sidebar-container" *ngIf="!isMobile">
        <mat-nav-list>
            <mat-list-item (click)="scrollTo('details', true)">
                <div class="list-content">
                    <span>Details</span>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('timeline')">
                <div class="list-content">
                    <span>Timeline</span>
                    <button class="add-button" mat-icon-button matTooltip="Add clinical note"
                        (click)="navigate($event, 'manageNote')" color="primary">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('ai_request')">
                <div class="list-content">
                    <span class="ai-highlight">AI Requests</span>
                    <button class="add-button" mat-icon-button matTooltip="Add AI note" (click)="navigate($event, 'ai')"
                        *ngIf="userRole !== 'Support-Limited'">
                        <mat-icon class="ai-feature">add</mat-icon>
                    </button>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('prescription')"
                [appGroupSpecialtyHide]="{ specialties: ['Physiotherapy'], condition: 'exclude' }">
                <div class="list-content">
                    <span>Medication</span>
                    <button class="add-button" mat-icon-button matTooltip="Add prescription"
                        (click)="navigate($event, 'managePrescription')" color="primary">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('lab_orders')">
                <div class="list-content">
                    <span>Lab Tests</span>
                    <button class="add-button" mat-icon-button matTooltip="Add Lab Test" *ngIf="!stateMode"
                        (click)="navigate($event, 'manageLabOrder')" color="primary">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('appointments')">
                <div class="list-content">
                    <span>Appointments</span>
                    <button class="add-button" mat-icon-button matTooltip="Add appointment"
                        (click)="navigate($event, 'manageAppointment')" color="primary">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('tasks')">
                <div class="list-content">
                    <span>Tasks</span>
                    <button class="add-button" mat-icon-button matTooltip="Add task"
                        (click)="navigate($event, 'manageTask')" color="primary">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('risk')">
                <div class="list-content">
                    <span>Risk Mgmt</span>
                    <button class="add-button" mat-icon-button matTooltip="Add risk item"
                        (click)="navigate($event, 'manageRiskItem')" color="primary">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('billing')" *ngIf="showCoding">
                <div class="list-content">
                    <span>Coding</span>
                    <button class="add-button" mat-icon-button matTooltip="Add procedure code"
                        (click)="navigate($event, 'manageCoding')" color="primary">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>
            </mat-list-item>
            <!-- HIDDEN FINANCIAL -->
            <mat-list-item (click)="scrollTo('invoicing')" *ngIf="!stateMode">
                <div class="list-content">
                    <span>Financials</span>
                    <button class="add-button" mat-icon-button matTooltip="Add quotation/invoice"
                        (click)="openFinanceForm()" color="primary">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('consent')">
                <div class="list-content">
                    <span>Consent</span>
                    <button class="add-button" mat-icon-button matTooltip="Add patient consent"
                        (click)="navigate($event, 'manageConsent')" color="primary">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('timeline')">
                <div class="list-content">
                    <span>M&M</span>
                    <button class="add-button" mat-icon-button matTooltip="Add Morbidity/Mortality"
                        (click)="navigate($event, 'manageMorbidityMortality')" color="primary">
                        <mat-icon>add</mat-icon>
                    </button>
                </div>
            </mat-list-item>

            <!--
                Practice documents (`tasks/practice-documents.md` §7). Placed after Consent and
                before Communication, which is where the PANEL sits in the page — the sidebar is a
                map of the page, so an item out of order sends somebody to the wrong place.

                The quick action is a SEND, not an add: a practice document is not authored here,
                it is chosen from the repository and sent to the patient. `manageDocument` is the
                same action the mobile FAB menu already offers, so both routes open the same
                dialog rather than growing a second one.
            -->
            <mat-list-item (click)="scrollTo('documents')">
                <div class="list-content">
                    <span>Documents</span>
                    <button class="add-button" mat-icon-button matTooltip="Send a document" *ngIf="!stateMode"
                        (click)="navigate($event, 'manageDocument')" color="primary">
                        <mat-icon>send</mat-icon>
                    </button>
                </div>
            </mat-list-item>

            <mat-list-item (click)="scrollTo('communication')">
                <div class="list-content">
                    <span>Communication</span>
                </div>
            </mat-list-item>

            <mat-list-item (click)="scrollTo('surveys')" *ngIf="showSurveys">
                <div class="list-content">
                    <span>Surveys</span>
                </div>
            </mat-list-item>

            <mat-list-item (click)="scrollTo('media')">
                <div class="list-content">
                    <span>Media</span>
                </div>
            </mat-list-item>
            <mat-list-item (click)="scrollTo('tags')">
                <div class="list-content">
                    <span>Tags</span>
                </div>
            </mat-list-item>
        </mat-nav-list>
    </div>

    <app-fab-menu *ngIf="patient && isMobile" [options]="menuOptions" [closeOnAction]="true">
    </app-fab-menu>
</div>

<ng-template #addColleagueDialog>
    <h1 mat-dialog-title>Colleague Details</h1>
    <div mat-dialog-content>
        <div class="p-t-20 field-column">
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Title</mat-label>
                <mat-select [(ngModel)]="addingColleague.title" required>
                    <mat-option *ngFor="let title of titles" [value]="title.value">
                        {{ title.text }}
                    </mat-option>
                </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Name</mat-label>
                <input matInput [(ngModel)]="addingColleague.name" placeholder="Name" type="text" required>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Surname</mat-label>
                <input matInput [(ngModel)]="addingColleague.surname" placeholder="Surname" type="text" required>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Speciality</mat-label>
                <mat-select [(ngModel)]="addingColleague.speciality">
                    <mat-option *ngFor="let speciality of specialities" [value]="speciality.value">
                        {{ speciality.text }}
                    </mat-option>
                </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Email</mat-label>
                <input matInput [(ngModel)]="addingColleague.email" placeholder="Email" type="email" required>
            </mat-form-field>
            <app-intl-phone-input [preferredCountries]="['za']" [providedValue]="addingColleague.contactNumber"
                placeholder="Contact Number" (valueChange)="addingColleague.contactNumber = $event" name="contactNumber"
                classList="full-width"></app-intl-phone-input>
        </div>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-raised-button color="primary" [mat-dialog-close]="addingColleague">Save</button>
    </div>

</ng-template>